// lib/productBadge.ts
// Decides the availability badge a product card shows (ui.productBadge).
//
// Pure on purpose: the one decision that matters here ("which label does THIS
// customer see, and when") must be unit-testable without rendering a card.
//
// Two sources:
//   'field'         → product[field] from our index. Same for every shopper.
//   'customerStock' → the per-customer stock text from the host's pricing
//                     response (syfak `stock_text`). This is what the host's own
//                     site shows that customer. syfak's labels genuinely differ
//                     per customer (measured 2026-10-01: product 100468 is
//                     «ΜΗΔΕΝΙΚΟ ΑΠΟΘΕΜΑ» in the feed but has NO label for the
//                     founder's account, on their site and in stock_text alike).

import type { ClientConfig } from '@/lib/clients/types';
import type { NormalizedPrice } from '@/lib/pricing/externalPricing';

export interface ProductBadge {
  label: string;
  background: string;
  textColor: string;
}

type BadgeConfig = NonNullable<ClientConfig['ui']['productBadge']>;

function indexedValue(product: Record<string, unknown>, field: string): string | null {
  const raw = product[field];
  const v = Array.isArray(raw) ? raw[0] : raw;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export function resolveProductBadge(
  cfg: BadgeConfig | undefined,
  product: Record<string, unknown>,
  customerPrice: NormalizedPrice | null | undefined,
  loadingCustomerPrice: boolean,
): ProductBadge | null {
  if (!cfg) return null;

  let value: string | null;
  if (cfg.source === 'customerStock') {
    // While this card's price is in flight, show nothing rather than the
    // indexed label: the live one may differ, and a badge that appears and then
    // vanishes reads as a glitch. The price itself shows a skeleton meanwhile.
    if (loadingCustomerPrice) return null;
    if (customerPrice && customerPrice.stockText !== undefined) {
      // A live answer — null means "no label" and wins over the index.
      value = customerPrice.stockText;
    } else {
      // No live answer: pricing off or failed (undefined), no price for this
      // customer / integrity-guard drop (null), or a response without
      // stock_text. The indexed value is the best we have.
      value = indexedValue(product, cfg.field);
    }
  } else {
    value = indexedValue(product, cfg.field);
  }

  if (!value) return null;
  const colors = cfg.values[value] ?? (cfg.defaultBackground ? { background: cfg.defaultBackground } : undefined);
  if (!colors) return null;
  return { label: value, background: colors.background, textColor: colors.textColor || '#ffffff' };
}
